import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import { useTheme } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { saveRewardsPlan } from '@react/fetchers/rewardsFetchers'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { toErc20Wei } from '@shared/conversions'
import { RewardsPlanCTO, RewardsPlanRowCTO } from '@shared/RewardsPlanCTO'
import { ZeroAddress } from 'ethers'
import React, { useEffect, useState } from 'react'

import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { DateRange, DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { toCompanyName } from '@react/fetchers/companyProfileFetchers'
import { logOT } from '@shared/loggingShared'
import { EthAddr, PoolAccountAccessPair } from '@shared/SchemaTypes'
import dayjs, { Dayjs } from 'dayjs'
import { CompanyProfileAddressItem } from './CompanyProfileAddressItem'

function RewardsEditRow(props: { rewardsPlanRow: RewardsPlanRowCTO; rowKey: string }): React.ReactElement {
  const { rewardsPlanRow, rowKey } = props
  const [minValue, setMinValue] = useState<string>(
    Math.round(Number(rewardsPlanRow.minimumPrincipalEarningInterest) / 1000000).toString()
  )
  const [interestRate, setInterestRate] = useState<string>(rewardsPlanRow.interestRate.toString())
  const [dailyInterestRate, setDailyInterestRate] = useState<string>(rewardsPlanRow.dailyInterestRate.toString())

  return (
    <TableRow key={rowKey}>
      <TableCell sx={{ paddingX: 1, textAlign: 'center' }}>
        <TextField
          size="small"
          label="Min PEI"
          value={minValue}
          onChange={(e) => {
            const value = e.target.value
            const numValue = Number(value)
            setMinValue(value)
            rewardsPlanRow.minimumPrincipalEarningInterest = toErc20Wei(numValue)
          }}
          sx={{ minWidth: 100 }}
        />
      </TableCell>
      <TableCell sx={{ paddingX: 1, textAlign: 'center' }}>
        <TextField
          size="small"
          label="Annal reward rate"
          value={interestRate}
          InputProps={{
            endAdornment: '%',
          }}
          onChange={(e) => {
            const value = e.target.value
            const numValue = Number(value)
            setInterestRate(value)
            rewardsPlanRow.interestRate = numValue
            rewardsPlanRow.dailyInterestRate = numValue / 365
            setDailyInterestRate(rewardsPlanRow.dailyInterestRate.toString())
          }}
          sx={{ minWidth: 100 }}
        />
      </TableCell>
      <TableCell sx={{ paddingX: 1, textAlign: 'center' }}>
        <Typography variant="caption">{Number(dailyInterestRate).toFixed(4)} %</Typography>
      </TableCell>
      <TableCell sx={{ paddingX: 1, textAlign: 'center' }}>DELETE</TableCell>
    </TableRow>
  )
}

// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
export default function RewardsEditForm(props: {
  rewardsPlan: Partial<RewardsPlanCTO>
  existingPlans: RewardsPlanCTO[]
  participants: PoolAccountAccessPair[]
  onSave: () => void
}): React.ReactElement {
  const UNSELECTED = 'UNSELECTED'
  const [rewardsPlanInEdit, setRewardsPlanInEdit] = useState<Partial<RewardsPlanCTO>>(props.rewardsPlan)
  const companyProfiles = useOtSelector((state) => state.poolState.companyProfiles)
  const [selectedParticipant, setSelectedParticipant] = React.useState<EthAddr>(UNSELECTED)
  const theme = useTheme()
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },

    cognitoUser,
  } = useOtChainState()
  useEffect(() => {
    setRewardsPlanInEdit(props.rewardsPlan)
  }, [props.rewardsPlan])
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs(rewardsPlanInEdit.startDateString),
    dayjs(rewardsPlanInEdit.endDateString),
  ])

  const plansForAccount: RewardsPlanCTO[] = props.existingPlans.filter(
    (rp) => rp.accountAddr == rewardsPlanInEdit.accountAddr
  )
  if (!rewardsPlanInEdit) {
    return (
      <Paper sx={{ padding: 3, border: '1px solid', borderColor: 'grey.200' }}>
        <Box data-testid="rewards-edit-form">
          <Typography variant="h3">NO PLAN</Typography>
        </Box>
      </Paper>
    )
  }

  const localSetDateRange = (newDateRange: DateRange<Dayjs>) => {
    const newRewardsPlan = { ...rewardsPlanInEdit }
    newRewardsPlan.startDateString = newDateRange[0]!.format('YYYY-MM-DD')
    newRewardsPlan.endDateString = newDateRange[1]!.format('YYYY-MM-DD')
    setRewardsPlanInEdit(newRewardsPlan)
    setDateRange([newDateRange[0]!, newDateRange[1]!])
  }

  const datesOverlap = plansForAccount.some((plan) => {
    if (plan.uuid === rewardsPlanInEdit.uuid) {
      return false
    }

    if (
      dayjs(plan.startDateString).isAfter(dayjs(rewardsPlanInEdit.startDateString)) &&
      dayjs(plan.startDateString).isBefore(dayjs(rewardsPlanInEdit.endDateString))
    ) {
      return true
    }

    if (
      dayjs(rewardsPlanInEdit.startDateString).isAfter(dayjs(plan.startDateString)) &&
      dayjs(rewardsPlanInEdit.startDateString).isBefore(dayjs(plan.endDateString))
    ) {
      return true
    }
    return false
  })
  return (
    <Paper sx={{ padding: 3, width: '100%', border: '4px solid', background: theme.palette.background.canvas }}>
      <Box
        data-testid="rewards-edit-form"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {!props.rewardsPlan.accountAddr && (
          <Box>
            <FormControl sx={{ m: 1, width: '80%' }}>
              <InputLabel id="asset-label">Lender</InputLabel>
              <Select
                labelId="asset-label"
                id="asset-select"
                input={<OutlinedInput id="asset-select" label="Asset" />}
                value={selectedParticipant}
                renderValue={(value: string) => {
                  if (value === UNSELECTED) {
                    return (
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        --- Select Lender ---
                      </Typography>
                    )
                  }
                  if (value === ZeroAddress) {
                    return (
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        Default rewards for all lenders
                      </Typography>
                    )
                  }
                  return <CompanyProfileAddressItem walletAddr={value} />
                }}
                onChange={(event: SelectChangeEvent) => {
                  logOT.info('event.target.value', event.target.value)
                  setSelectedParticipant(event.target.value as string)
                  const accountAddr = event.target.value as EthAddr
                  const newRewardsPlan = { ...rewardsPlanInEdit } as RewardsPlanCTO
                  newRewardsPlan.accountAddr = accountAddr
                  newRewardsPlan.companyName = toCompanyName(accountAddr, companyProfiles)
                  newRewardsPlan.poolAddrLenderAddr = `${props.rewardsPlan.poolAddr}-${accountAddr}`
                  setRewardsPlanInEdit(newRewardsPlan)
                }}
              >
                <MenuItem
                  value={UNSELECTED}
                  key={UNSELECTED}
                  style={{
                    paddingLeft: 12,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      borderWidth: 1,
                      borderColor: 'grey.500',
                      borderStyle: 'solid',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                      padding: 6,
                    }}
                  >
                    <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                      --- Select Lender ---
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem
                  value={ZeroAddress}
                  key={`asset-option-${ZeroAddress}`}
                  style={{
                    paddingLeft: 12,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      borderWidth: 1,
                      borderColor: 'grey.500',
                      borderStyle: 'solid',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                      padding: 6,
                    }}
                  >
                    <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                      Default rewards for all lenders
                    </Typography>
                  </Box>
                </MenuItem>
                {props.participants
                  // .filter((p: PoolAccountAccessPair) => rewardsPlans.find((rp) => rp.accountAddr === p.accountAddr))
                  .map((p: PoolAccountAccessPair) => (
                    <MenuItem
                      value={p.accountAddr}
                      key={`asset-option-${p.accountAddr}`}
                      style={{
                        paddingLeft: 12,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      <CompanyProfileAddressItem hasBorder={true} walletAddr={p.accountAddr} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        )}
        <Box
          data-testid="rewards-edit-form"
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            justifyContent: 'space-between',
          }}
        >
          {rewardsPlanInEdit.accountAddr !== ZeroAddress && (
            <Typography variant="bodyBold">{rewardsPlanInEdit.companyName}</Typography>
          )}
          {rewardsPlanInEdit.accountAddr == ZeroAddress && (
            <Typography variant="bodyBold">Default rewards for all lenders</Typography>
          )}

          <Button
            disabled={datesOverlap || rewardsPlanInEdit.rewardsPlanRows?.length === 0 || !rewardsPlanInEdit.accountAddr}
            sx={{ marginRight: 1 }}
            variant="contained"
            color="primary"
            onClick={() => {
              if (cognitoUser) {
                const newRewardsPlan = { ...rewardsPlanInEdit } as RewardsPlanCTO
                newRewardsPlan.rewardsPlanRows!.sort(
                  (a, b) => Number(a.minimumPrincipalEarningInterest) - Number(b.minimumPrincipalEarningInterest)
                )
                setRewardsPlanInEdit(newRewardsPlan)
                saveRewardsPlan(serverApiBaseUrl, restAPIKey, cognitoUser, newRewardsPlan).then(() => {
                  props.onSave()
                })
              }
            }}
          >
            Save
          </Button>
        </Box>
        <Box sx={{ flexGrow: 0, width: 275, flexDirection: 'column', display: 'flex', marginBottom: 6 }}>
          <Typography sx={{ marginTop: -1, marginBottom: 1, height: 24 }} variant="caption">
            Date Range
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="UTC">
            <DateRangePicker
              sx={{
                height: 40,
              }}
              calendars={2}
              label={'Date Range'}
              value={dateRange}
              onChange={(newDateRange) => {
                localSetDateRange(newDateRange)
              }}
            />
          </LocalizationProvider>
          {datesOverlap && (
            <Typography variant="caption" color="error">
              Dates overlap
            </Typography>
          )}
        </Box>
        <Table key="table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ paddingX: 2 }}>
                <Typography variant="caption">Minimum PEI</Typography>
              </TableCell>
              <TableCell sx={{ paddingX: 2 }}>
                <Typography variant="caption">Annual Rate</Typography>
              </TableCell>
              <TableCell sx={{ paddingX: 1, textAlign: 'center' }}>
                <Typography variant="caption">Daily Rate</Typography>
              </TableCell>
              <TableCell sx={{ paddingX: 1, textAlign: 'right' }}>
                <Typography variant="caption">Action</Typography>
                <Button
                  sx={{ marginLeft: 2, marginRight: 2 }}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    const newRewardsPlan = { ...rewardsPlanInEdit }
                    newRewardsPlan.rewardsPlanRows!.push({
                      minimumPrincipalEarningInterest: '0',
                      interestRate: 0,
                      dailyInterestRate: 0,
                    })
                    setRewardsPlanInEdit(newRewardsPlan)
                  }}
                >
                  Add a range
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rewardsPlanInEdit.rewardsPlanRows
              ?.sort((a, b) => Number(a.minimumPrincipalEarningInterest) - Number(b.minimumPrincipalEarningInterest))
              .map((rewardsPlanRow, index) => (
                <RewardsEditRow key={'Row' + index} rowKey={'Row' + index} rewardsPlanRow={rewardsPlanRow} />
              ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  )
}
